import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

const faqs = [
    { q: "How does onboarding work?", a: "We start with a deep discovery to understand your workflows, systems, and how your staff works today. From there, we handle integrations, data mapping, and tailored workflow design — then thoroughly test every deployment before going live." },
    { q: "How does Lexafide's pricing work?", a: "We begin with a low-cost pilot phase to prove value quickly. Once workflows are validated, pricing transitions to a monthly plan aligned with anticipated usage and deployed capabilities — with generous thresholds." },
    { q: "Can you integrate with our systems and AI providers?", a: "Yes. Lexafide is built as an orchestration layer, allowing us to integrate with your existing systems and leverage best-in-class AI providers." },
    { q: "Does Lexafide handle SMS and email too?", a: "Yes. In addition to voice, Lexafide supports SMS and email workflows to ensure consistent communication across every channel." },
    { q: "Are agents handling conversations or just tracking them?", a: "Both. Lexafide can actively handle conversations end-to-end or operate in the background to track, transcribe, and assist human teams in real time." },
    { q: "What if we don't want AI speaking to our clients?", a: "We take a consultative approach: human-assist workflows where AI supports your team without speaking to clients — or autonomous agents when you're ready." },
];

const FAQSection = () => {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section data-testid="faq-section" className="relative py-24 md:py-32 bg-[var(--cream-2)] border-t border-[var(--border)]">
            <div className="max-w-[980px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mb-12"
                >
                    <span className="eyebrow-pill">FAQ</span>
                    <h2 className="font-display text-[clamp(2rem,4.2vw,3.2rem)] leading-[1.04] tracking-tight font-semibold text-[var(--ink)] mt-5">
                        Questions? <span className="accent-blue">We&apos;re glad you asked.</span>
                    </h2>
                </motion.div>

                <div className="space-y-3">
                    {faqs.map((f, i) => {
                        const isOpen = openIndex === i;
                        return (
                            <motion.div
                                key={f.q || f.question || i}
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                data-testid={`faq-item-${i}`}
                                className={`card-paper overflow-hidden ${isOpen ? "border-[var(--coral)]/30" : ""}`}
                            >
                                <button
                                    type="button"
                                    onClick={() => setOpenIndex(isOpen ? -1 : i)}
                                    className="w-full flex items-center justify-between text-left px-6 md:px-8 py-5 md:py-6 group"
                                    data-testid={`faq-toggle-${i}`}
                                >
                                    <span className={`font-display text-lg md:text-xl pr-6 transition-colors ${isOpen ? "text-[var(--ink)]" : "text-[var(--ink-soft)] group-hover:text-[var(--ink)]"}`}>
                                        {f.q}
                                    </span>
                                    <motion.div
                                        animate={{ rotate: isOpen ? 45 : 0 }}
                                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                        className="w-8 h-8 rounded-full border border-[var(--border-strong)] flex items-center justify-center flex-shrink-0"
                                        style={{ color: isOpen ? "var(--coral)" : "var(--ink-dim)" }}
                                    >
                                        <i className="fa-solid fa-plus text-xs" />
                                    </motion.div>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                            className="overflow-hidden"
                                        >
                                            <p className="px-6 md:px-8 pb-6 md:pb-7 text-[var(--ink-soft)] leading-relaxed max-w-3xl">
                                                {f.a}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
